/**
 * ClipShare History Service
 * Manages clipboard visit history in localStorage
 */

const HISTORY_KEY = 'clipshare_history';
const MAX_HISTORY_SIZE = 20;

class HistoryService {
  /**
   * Add or update a clipboard in history
   * @param {string} clipboardId - The unique ID of the clipboard
   * @param {string} clipboardName - The name of the clipboard
   */
  addToHistory(clipboardId, clipboardName = 'Untitled Clipboard') {
    try {
      const history = this.getHistory();
      const now = new Date().toISOString();
      
      // Find existing entry
      const existingIndex = history.findIndex(item => item.id === clipboardId);
      
      if (existingIndex !== -1) {
        // Update existing entry
        const existing = history[existingIndex];
        existing.name = clipboardName; // Update name in case it changed
        existing.lastVisited = now;
        existing.visitCount = (existing.visitCount || 1) + 1;
        
        // Move to front
        history.splice(existingIndex, 1);
        history.unshift(existing);
      } else {
        // Add new entry at the front
        history.unshift({
          id: clipboardId,
          name: clipboardName,
          lastVisited: now,
          visitCount: 1
        });
      }
      
      // Limit history size
      if (history.length > MAX_HISTORY_SIZE) {
        history.splice(MAX_HISTORY_SIZE);
      }
      
      this.saveHistory(history);
    } catch (error) {
      console.error('Error adding to clipboard history:', error);
    }
  }

  /**
   * Get the clipboard history
   * @returns {Array} Array of history items sorted by lastVisited (newest first)
   */
  getHistory() {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (!stored) return [];
      
      const history = JSON.parse(stored);
      if (!Array.isArray(history)) return [];
      
      // Sort by lastVisited (newest first) and validate structure
      return history
        .filter(item => item && item.id && item.lastVisited)
        .sort((a, b) => new Date(b.lastVisited) - new Date(a.lastVisited));
    } catch (error) {
      console.error('Error reading clipboard history:', error);
      // Clear corrupted history
      this.clearHistory();
      return [];
    }
  }

  /**
   * Remove a specific clipboard from history
   * @param {string} clipboardId - The unique ID of the clipboard to remove
   */
  removeFromHistory(clipboardId) {
    try {
      const history = this.getHistory();
      const filtered = history.filter(item => item.id !== clipboardId);
      this.saveHistory(filtered);
    } catch (error) {
      console.error('Error removing from clipboard history:', error);
    }
  }

  /**
   * Clear all clipboard history
   */
  clearHistory() {
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch (error) {
      console.error('Error clearing clipboard history:', error);
    }
  }

  /**
   * Get the current history size
   * @returns {number} Number of items in history
   */
  getHistorySize() {
    return this.getHistory().length;
  }

  /**
   * Save history to localStorage
   * @private
   * @param {Array} history - The history array to save
   */
  saveHistory(history) {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving clipboard history:', error);
      // If localStorage is full or unavailable, try to clear old entries
      if (error.name === 'QuotaExceededError') {
        try {
          // Keep only the 10 most recent entries
          const reduced = history.slice(0, 10);
          localStorage.setItem(HISTORY_KEY, JSON.stringify(reduced));
        } catch (retryError) {
          console.error('Failed to save even reduced history:', retryError);
        }
      }
    }
  }

  /**
   * Format relative time for display
   * @param {string} isoString - ISO date string
   * @returns {string} Human-readable relative time
   */
  formatRelativeTime(isoString) {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMinutes < 1) {
        return 'Just now';
      } else if (diffMinutes < 60) {
        return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        // For older dates, show the actual date
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error('Error formatting relative time:', error);
      return 'Unknown';
    }
  }

  /**
   * Check if localStorage is available
   * @returns {boolean} True if localStorage is available
   */
  isStorageAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
export const historyService = new HistoryService();
export default historyService;
